import {
  ColumnDef,
  functionalUpdate,
  Row,
  SortingState,
  Updater,
} from '@tanstack/react-table';
import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { EventAccordionItemBodyComponentType } from 'src/components/Accordions';
import { shiftSelect } from 'src/components/Events/EventPage/EventPage.css';
import {
  createAggregatedListing,
  createFakeListingGroup,
} from 'src/components/Listings/listingUtils';
import { useAppContext } from 'src/contexts/AppContext';
import { ColumnResizingContextProvider } from 'src/contexts/ColumnResizingContext/ColumnResizingContext';
import {
  Content,
  FormatContent,
  useContent,
} from 'src/contexts/ContentContext';
import { MultiSelectScope } from 'src/contexts/MultiSelectionContext';
import { useEnableSelectionByDefault } from 'src/hooks/useEnableSelectionByDefault';
import { usePosLiteAccount } from 'src/hooks/usePosLiteAccount';
import { useSortListingTableSeatingColumn } from 'src/hooks/useSortListingTableSeatingColumn';
import { useTagsForEntityType } from 'src/hooks/useTagsForEntityType';
import {
  useUserHasFeature,
  useUserHasTableTotalsFeature,
} from 'src/hooks/useUserHasFeature';
import { useUserHasConsignorMasterUI } from 'src/hooks/useUserIsConsignorMasterUI';
import { useServerUserSetting } from 'src/hooks/useUserSetting';
import { FormatOption, FormatOptionEntries } from 'src/modals/EditTableColumns';
import {
  flattenListingGroup,
  sortGroupType,
} from 'src/modals/GroupListings/components/groupingUtils';
import { GroupIdNameMap } from 'src/modals/MergeListings/utils';
import { inventoryEventDetailsTableCollapsed } from 'src/tables/ListingTable/ListingTable.css';
import { NoData } from 'src/tables/Table';
import {
  defaultListingColumnsConfig,
  filterColumnsByFeatures,
  getListingColumnConfigById,
} from 'src/utils/columns/columnUtils';
import { LISTING_MAIN_COLUMNS_V2 } from 'src/utils/columns/inventory/inventoryColumnUtils.constants';
import { ListingTableColumnId } from 'src/utils/columns/inventory/inventoryColumnUtils.types';
import { filterCustomColumnsForListing } from 'src/utils/columns/inventory/inventoryCustomColumnUtils';
import { CustomListingColumn } from 'src/utils/columns/inventory/inventoryCustomColumnUtils.types';
import { ContentId } from 'src/utils/constants/contentId';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import { generateShimmeringRows } from 'src/utils/dataTableUtils';
import { isInFullPageEventView } from 'src/utils/deepLinkUtils';
import { SectionType } from 'src/utils/types/sectionType';
import { hasAllPermissions } from 'src/utils/userPermissionUtils';
import { hasFeatures } from 'src/utils/userUtils';
import { SomethingWentWrong } from 'src/views';
import {
  ActionOutboxEntityType,
  Feature,
  GroupType,
  IListingGroupItem,
  Listing,
  ListingGroup,
  ListingStatus,
  UserSetting,
} from 'src/WebApiController';

import { GroupedTable } from '../TableV2/GroupedTable';
import {
  customColumnDef,
  tagColumnDef,
} from './configs/ListingTableColumnsConfig';
import { getListingTableColumnConfigsV2 } from './configs/ListingTableColumnsConfigV2';
import { ListingTableGroupAffordanceHeader } from './configs/ListingTableGroupAffordanceHeader';
import { ListingTableGroupHeader } from './configs/ListingTableGroupHeader';
import { useListingGroupMetrics } from './hooks/useListingGroupMetrics';
import { useNewGroupScroll } from './hooks/useNewGroupScroll';
import {
  getRowId,
  processListingGroupedRows,
  useRowOperations,
  useTableData,
  useTableRowWrapper,
  useTableState,
} from './ListingTable.hooks';
import { ListingWithEvent } from './ListingTable.types';
import { ListingTableActionContextWrapper } from './ListingTableActionContext';

export type ListingTableProps = EventAccordionItemBodyComponentType<Listing> & {
  getListingNotEligibleForSelection?: (
    listings: Listing[] | null
  ) => { listing: Listing; ineligibleReason: string | undefined }[] | undefined;
  showMainColumnsOnly?: boolean;
  hideUnselectableListings?: boolean;
  showUnselectableListingsOnly?: boolean;
  disablePagination?: boolean;
  compListings?: Listing[];
  getListingTicketClassColor?: (listing: Listing) => string | undefined;
  listingGroupMap?: GroupIdNameMap;
  isSideTable?: boolean;
  alwaysShowCheckbox?: boolean;
  showSelectAllOnHeader?: boolean;
};

export const PAGE_SIZE = 10;

export const ListingTableV2 = ({
  event,
  entities: listings,
  entitiesSummary: listingsSummary,
  compListings,
  entityCount: listingCount,
  ungroupedListingCount,
  listingGroupCount,
  getDataFail,
  selectedIds,
  selectionMode: defaultSelectionMode,
  onItemSelectionsChanged,
  getListingNotEligibleForSelection,
  hideUnselectableListings,
  showUnselectableListingsOnly,
  showMainColumnsOnly,
  disablePagination,
  onMount,
  onUnmount,
  initState,
  useVirtuoso,
  getListingTicketClassColor,
  tableWithOuterPadding,
  useTableNavKeys = false,
  listingGroupMap,
  isSideTable,
  alwaysShowCheckbox,
  showSelectAllOnHeader,
  enableColumnFilters,
}: ListingTableProps) => {
  const enableSelectionByDefault = useEnableSelectionByDefault();
  alwaysShowCheckbox = alwaysShowCheckbox || enableSelectionByDefault;

  const hasTableTotalsFeature = useUserHasTableTotalsFeature(
    ActionOutboxEntityType.Listing
  );

  const hasConsignorMasterUI = useUserHasConsignorMasterUI();
  const hasEnableOffice = useUserHasFeature(Feature.EnableOffice);
  const isPosLite = usePosLiteAccount();

  const { appContext, loginContext, mainRoute } = useAppContext();
  const inFullPageEventView = isInFullPageEventView(mainRoute);
  const listingText = useContent(ContentId.Listings);
  const isItemsLoading =
    listingCount > 0 && (listings == null || listings.length == 0);
  const hasSortListingsInGroupFeature = useUserHasFeature(
    Feature.SortListingsInGroup
  );

  const soldGroupName = useContent(ContentId.Sold);
  const isGroupSoldListingsEnabled = useUserHasFeature(
    Feature.GroupSoldListings
  );
  const hasReplaceUnsoldWithAvailableTicketsFeature = useUserHasFeature(
    Feature.ReplaceUnsoldWithAvailableTickets
  );
  const hasExpandGroupFromClick = useUserHasFeature(
    Feature.ExpandGroupFromClick
  );
  const hideHiddenByFilter = useUserHasFeature(Feature.HideHiddenByFilter);

  // Store mapping of sold listing IDs to their parent group names
  const soldListingToGroupNameMapRef = useRef<Map<number, string>>(new Map());

  const { filteredListings, mapListingWithEvent } = useTableData(
    event,
    listings,
    compListings,
    listingGroupMap,
    hideUnselectableListings,
    showUnselectableListingsOnly,
    getListingNotEligibleForSelection,
    getListingTicketClassColor
  );

  const data = useMemo<(ListingWithEvent | null)[]>(() => {
    let result = filteredListings;

    if (isGroupSoldListingsEnabled && result) {
      // Get ALL sold listings (ungrouped AND grouped)
      const allSoldListings: ListingWithEvent[] = [];
      const remainingListings: ListingWithEvent[] = [];
      // Map to store listing ID -> parent group name
      const soldListingToGroupNameMap = new Map<number, string>();

      result.forEach((item) => {
        if (!item) return;
        if (!item.isLtGrp) {
          // Regular listing
          if (item.listing?.status === ListingStatus.FullySold) {
            allSoldListings.push(item);
          } else {
            remainingListings.push(item);
          }
        } else {
          // This is a group - need to extract sold listings from it
          const group = item.listing as ListingGroup;
          const soldFromGroup: Listing[] = [];
          const remainingInGroup: IListingGroupItem[] = [];

          group.groupItems?.forEach((groupItem: IListingGroupItem) => {
            const isGroupSold =
              groupItem.isLtGrp &&
              (groupItem as ListingGroup).groupType === GroupType.FullySold;

            if (isGroupSold) {
              const soldGroup = groupItem as ListingGroup;
              soldGroup.groupItems?.forEach((item) => {
                if (!item.isLtGrp) {
                  soldFromGroup.push(item as Listing);
                }
              });
            } else {
              remainingInGroup.push(groupItem);
            }
          });

          // Add sold listings to collection
          soldFromGroup.forEach((soldListing) => {
            // Store the mapping of listing ID to parent group name
            soldListingToGroupNameMap.set(soldListing.id, group.groupName);
            allSoldListings.push({
              ...item,
              listing: soldListing,
              isLtGrp: false,
              ltGroupName: group.groupName,
            });
          });

          // If group still has remaining items, keep it
          if (remainingInGroup.length > 0) {
            const updatedGroup: ListingGroup = {
              ...group,
              groupItems: remainingInGroup,
            };
            remainingListings.push({
              ...item,
              listing: updatedGroup,
            });
          }
        }
      });

      // Create the Sold group if we have sold listings
      if (allSoldListings.length > 0) {
        // Update the ref with the new mapping
        soldListingToGroupNameMapRef.current = soldListingToGroupNameMap;

        const soldGroup = createFakeListingGroup(
          allSoldListings,
          soldGroupName
        );
        if (soldGroup) {
          result = [...remainingListings, soldGroup];
        } else {
          result = remainingListings;
        }
      } else {
        result = remainingListings;
      }
    }

    return result?.length
      ? result
      : result == null || isItemsLoading
        ? (generateShimmeringRows(
            ungroupedListingCount ?? 1,
            listingGroupCount ?? 0
          ) as (ListingWithEvent | null)[])
        : result;
  }, [
    filteredListings,
    isGroupSoldListingsEnabled,
    soldGroupName,
    isItemsLoading,
    ungroupedListingCount,
    listingGroupCount,
  ]);

  const {
    sorting,
    setSorting,
    expandedState,
    setExpandedState,
    pagination,
    setPagination,
  } = useTableState(
    event,
    listings,
    listingCount,
    data,
    disablePagination,
    initState,
    onMount,
    onUnmount,
    true
  );

  const {
    selectionMode,
    showCheckbox,
    visibleRowIds,
    rowSelection,
    listingSelection,
    isShiftKeyPressed,
    onRowSelect,
    onRowHotkeySelect,
    onRowShiftSelect,
    onFilteredRowsChange,
    onRowSelectionChange,
  } = useRowOperations(
    event,
    data,
    selectedIds,
    defaultSelectionMode,
    isSideTable,
    alwaysShowCheckbox,
    onItemSelectionsChanged,
    true
  );

  const onSortingChange = useCallback(
    (updater: Updater<SortingState>) => {
      const newSorting = functionalUpdate(updater, sorting);
      setSorting(newSorting);
    },
    [setSorting, sorting]
  );

  const {
    value: storedInventoryColumnOrderSetting = defaultListingColumnsConfig,
  } = useServerUserSetting<ListingTableColumnId[]>({
    id: isSideTable
      ? UserSetting.InventorySideTableColumnOrder
      : UserSetting.InventoryColumnOrder,
  });
  const { value: storedInventoryColumnNumberPrecisions = {} } =
    useServerUserSetting<FormatOptionEntries>({
      id: isSideTable
        ? UserSetting.InventorySideTableColumnNumberPrecision
        : UserSetting.InventoryColumnNumberPrecision,
    });

  const { value: storedInventoryColumnsEnabledSetting } = useServerUserSetting<
    string[]
  >({
    id: isSideTable
      ? UserSetting.InventorySideTableColumnsEnabled
      : UserSetting.InventoryColumnsEnabled,
  });

  const { value: customListingColumns = [] } = useServerUserSetting<
    CustomListingColumn[]
  >({
    id: UserSetting.InventoryCustomColumns,
  });
  const { tagsMetadata, tagsMetadataNumeric } = useTagsForEntityType(
    ActionOutboxEntityType.Listing,
    true
  );

  const customListingColumnsFiltered = filterCustomColumnsForListing(
    customListingColumns,
    tagsMetadataNumeric
  );

  const { value: editableColumns = [] } = useServerUserSetting<string[]>({
    id: UserSetting.InventoryColumnEditability,
  });

  // With replacing column feature, we want to sanitized the settings and replace the column in the user settings too
  const [sanitizedInventoryColumnOrder, sanitizedInventoryColumnEnabled] =
    useMemo(() => {
      if (!hasReplaceUnsoldWithAvailableTicketsFeature) {
        return [
          storedInventoryColumnOrderSetting,
          storedInventoryColumnsEnabledSetting,
        ];
      }
      const sanitizedColumnOrder: ListingTableColumnId[] =
        storedInventoryColumnOrderSetting.map((columnId) =>
          columnId === ListingTableColumnId.UnsoldQuantity
            ? ListingTableColumnId.AvailableQuantity
            : columnId
        );
      const sanitizedColumnEnabled = storedInventoryColumnsEnabledSetting?.map(
        (columnId) =>
          columnId === ListingTableColumnId.UnsoldQuantity
            ? ListingTableColumnId.AvailableQuantity
            : columnId
      );
      return [sanitizedColumnOrder, sanitizedColumnEnabled];
    }, [
      hasReplaceUnsoldWithAvailableTicketsFeature,
      storedInventoryColumnOrderSetting,
      storedInventoryColumnsEnabledSetting,
    ]);

  const editableColumnsSet = useMemo(
    () => new Set(editableColumns),
    [editableColumns]
  );

  const hasGroup = useMemo(
    () => listings?.find((l) => l.isLtGrp) != null,
    [listings]
  );

  const allColumnConfigs = useMemo(
    () =>
      getListingTableColumnConfigsV2(
        hasSortListingsInGroupFeature,
        hasGroup,
        false,
        editableColumnsSet,
        inFullPageEventView
      ),
    [
      hasSortListingsInGroupFeature,
      hasGroup,
      editableColumnsSet,
      inFullPageEventView,
    ]
  );

  const displayedColumnsConfig = useMemo(() => {
    if (showMainColumnsOnly) {
      return LISTING_MAIN_COLUMNS_V2.filter((c) => {
        // Only show Seating Row and Seats if Seating is set to Section Only
        if (
          c === ListingTableColumnId.SeatingRow ||
          c === ListingTableColumnId.SeatingSeats
        ) {
          return (
            storedInventoryColumnNumberPrecisions[
              ListingTableColumnId.Seating
            ] === FormatOption.Seating_SectionOnly
          );
        }

        if (c === ListingTableColumnId.ConsigneeSellerAccountId) {
          return hasConsignorMasterUI;
        }
        if (c === ListingTableColumnId.Office) {
          return hasEnableOffice;
        }
        return true;
      }).reduce<ColumnDef<ListingWithEvent>[]>((acc, columnId) => {
        const columnConfig = Object.values(allColumnConfigs).find(
          (column) => column.id === columnId
        );
        if (columnConfig) {
          acc.push(columnConfig as ColumnDef<ListingWithEvent>);
        }
        return acc;
      }, []);
    }

    const inventoryColumnOrder = sanitizedInventoryColumnOrder.filter(
      // If storedInventoryColumnsEnabledSetting has never been set, default to true
      (c) => sanitizedInventoryColumnEnabled?.includes(c) ?? true
    );

    const columns = new Set([
      // Add unconfigurable columns first
      ...Object.values(allColumnConfigs)
        .filter((column) => {
          const columnDef = getListingColumnConfigById(
            column.id as ListingTableColumnId
          ).personalization;

          const hasFeatureForColumn =
            columnDef.requiredFeatures.length === 0 ||
            hasFeatures(
              loginContext?.user,
              appContext?.features,
              columnDef.requiredFeatures
            );
          const hasPermissionForColumn =
            columnDef.requiredPermissions.length === 0 ||
            hasAllPermissions(
              loginContext?.user,
              columnDef.requiredPermissions
            );

          const isConfigurable = columnDef.isConfigurable;
          return (
            !isConfigurable && hasFeatureForColumn && hasPermissionForColumn
          );
        })
        .map((item) => item.id as ListingTableColumnId),
      // Add user defined columns next
      ...filterColumnsByFeatures(
        inventoryColumnOrder,
        SectionType.Listings,
        customListingColumnsFiltered,
        loginContext?.user,
        appContext?.features,
        tagsMetadata ?? []
      ),
    ]);

    if (isPosLite) {
      columns.delete(ListingTableColumnId.Actions);
    }

    if (showCheckbox) {
      if (!alwaysShowCheckbox) {
        columns.delete(ListingTableColumnId.Actions);
      }
    } else {
      columns.delete(ListingTableColumnId.Checkbox);
    }

    return Array.from(columns).reduce<ColumnDef<ListingWithEvent>[]>(
      (acc, columnId) => {
        if (tagsMetadata?.find((t) => t.key === columnId)) {
          acc.push(tagColumnDef(columnId) as ColumnDef<ListingWithEvent>);
        } else if (
          customListingColumnsFiltered.find((c) => c.id === columnId)
        ) {
          acc.push(
            customColumnDef(
              columnId,
              customListingColumnsFiltered.find((c) => c.id === columnId)
                ?.formula
            ) as ColumnDef<ListingWithEvent>
          );
        } else {
          const columnConfig = Object.values(allColumnConfigs).find(
            (column) => column.id === columnId
          );
          if (columnConfig) {
            acc.push(columnConfig as ColumnDef<ListingWithEvent>);
          }
        }

        return acc;
      },
      []
    );
  }, [
    showMainColumnsOnly,
    sanitizedInventoryColumnOrder,
    allColumnConfigs,
    customListingColumnsFiltered,
    loginContext?.user,
    appContext?.features,
    tagsMetadata,
    showCheckbox,
    storedInventoryColumnNumberPrecisions,
    hasConsignorMasterUI,
    hasEnableOffice,
    sanitizedInventoryColumnEnabled,
    alwaysShowCheckbox,
    isPosLite,
  ]);

  const { onRowWrapper, onRowWrapperV2 } = useTableRowWrapper(
    event,
    pagination,
    displayedColumnsConfig,
    true
  );

  const sectionType = isSideTable
    ? SectionType.InventorySideTable
    : SectionType.Listings;

  const seatingColumnSorting = useSortListingTableSeatingColumn(sectionType);

  // Memoize heavy state objects separately
  const tableState = useMemo(
    () => ({
      pagination,
      sorting,
      rowSelection,
      expanded: expandedState,
      columnVisibility: {
        [ListingTableColumnId.TicketClassColor]:
          getListingTicketClassColor != null,
      },
    }),
    [
      pagination,
      sorting,
      rowSelection,
      expandedState,
      getListingTicketClassColor,
    ]
  );

  const listingsSummaryFiltered = useMemo(() => {
    if (!listingsSummary || !hasTableTotalsFeature) {
      return null;
    }

    if (visibleRowIds) {
      // If there's client-side filtering, the best we can do is aggregate the sales and skip those with different currency
      return createAggregatedListing(
        data
          ?.filter((l) => !l?.isCompListing)
          ?.flatMap((l) => flattenListingGroup(l?.listing))
          .filter((l) => l?.isFull)
          .map((l) =>
            mapListingWithEvent({
              listing: l as Listing,
              ineligibleReason: undefined,
            })
          )
          .filter(
            (d, i) =>
              d?.listing?.isFull &&
              visibleRowIds.includes(Number(getRowId(d, i)))
          )
          .map((d) => d!)
      );
    }
    return mapListingWithEvent({
      listing: listingsSummary,
      ineligibleReason: undefined,
    });
  }, [
    listingsSummary,
    visibleRowIds,
    mapListingWithEvent,
    data,
    hasTableTotalsFeature,
  ]);

  const tableMeta = useMemo(
    () => ({
      sectionType,
      showSelectAllCheckbox:
        selectionMode?.mode === MultiSelectScope.SingleGroup &&
        showSelectAllOnHeader,
      summaryData: listingsSummaryFiltered,
    }),
    [
      sectionType,
      showSelectAllOnHeader,
      listingsSummaryFiltered,
      selectionMode?.mode,
    ]
  );

  const groupMetricsMap = useListingGroupMetrics(data);

  const renderGroupHeader = useCallback(
    (groupId: string) => {
      const groupMetrics = groupMetricsMap.get(groupId);
      if (!groupMetrics) return null;

      const { groupName, allListings, metrics, status, listingGroup } =
        groupMetrics;

      if (status === ListingStatus.FullySold) {
        return (
          <ListingTableGroupHeader
            groupName={groupName}
            soldTickets={metrics.soldTickets}
          />
        );
      }

      return (
        <ListingTableGroupAffordanceHeader
          groupName={groupName}
          metrics={metrics}
          totalListingCount={allListings.length}
          event={event}
          listingGroup={listingGroup}
        />
      );
    },
    [groupMetricsMap, event]
  );

  const memoizedProcessRows = useCallback(
    (rows: Row<ListingWithEvent>[]) =>
      processListingGroupedRows(rows, (data) => Boolean(data?.isLtGrp)),
    []
  );

  const getSubRows = useCallback(
    (originalRow: ListingWithEvent) => {
      if (!originalRow?.listing || !originalRow.listing.isLtGrp) {
        return [];
      }
      const listingGroup = originalRow.listing as ListingGroup;
      return (
        [...(listingGroup.groupItems ?? [])]
          .filter(
            (l) =>
              !hideHiddenByFilter ||
              !l.isLtGrp ||
              (l as ListingGroup).groupType !== GroupType.Invisible
          )
          .toSorted((a, b) => {
            if (a.isLtGrp && b.isLtGrp) {
              return sortGroupType(
                (a as ListingGroup).groupType,
                (b as ListingGroup).groupType
              );
            } else if (a.isLtGrp || b.isLtGrp) {
              return a.isLtGrp ? -1 : 1; // listing group sort first
            }

            return (a.ltGrpPrior ?? -1) - (b.ltGrpPrior ?? -1);
          })
          ?.map((l, i) => {
            const listing = l as Listing;
            // Check if this listing is in the sold group mapping
            const parentGroupName = soldListingToGroupNameMapRef.current.get(
              listing.id
            );

            return {
              ...originalRow,
              rowIndex: i,
              listing: l,
              isLtGrp: l.isLtGrp,
              // Use the sold group mapping first, then try listingGroupMap, then fall back to originalRow
              ltGroupName:
                parentGroupName ||
                (listingGroupMap && listing.ltGrpId
                  ? listingGroupMap[listing.ltGrpId]
                  : originalRow.ltGroupName),
            } as ListingWithEvent;
          }) ?? []
      );
    },
    [hideHiddenByFilter, listingGroupMap]
  );

  const { scrollTargetRowId } = useNewGroupScroll({
    listings,
    event,
    onItemSelectionsChanged,
  });

  return getDataFail ? (
    <SomethingWentWrong
      message={<Content id={ContentId.FailToLoadListContent} />}
    />
  ) : data?.length ? (
    <ListingTableActionContextWrapper viagogoVirtualIds={event.virtId}>
      <ColumnResizingContextProvider
        userSettingId={UserSetting.InventoryColumnWidths}
      >
        <GroupedTable<ListingWithEvent>
          useVirtuoso={useVirtuoso}
          activeRowIds={new Set(selectedIds?.map((id) => id.toString()))}
          onRowSelect={onRowSelect}
          actionColId={ListingTableColumnId.Actions}
          data={data as ListingWithEvent[]}
          columns={displayedColumnsConfig}
          alwaysShowCheckbox={alwaysShowCheckbox}
          rowWrapper={onRowWrapper}
          rowWrapperV2={onRowWrapperV2}
          className={clsx({
            [inventoryEventDetailsTableCollapsed]:
              selectionMode?.mode === 'singleGroup',
            [shiftSelect]: isShiftKeyPressed,
          })}
          onRowSelectionChange={onRowSelectionChange}
          onExpandedChange={setExpandedState}
          onFilteredRowsChange={onFilteredRowsChange}
          pagination={
            disablePagination
              ? undefined
              : { pageSize: pagination.pageSize, variant: 'input' }
          }
          onPaginationChange={setPagination}
          onSortingChange={onSortingChange}
          meta={tableMeta}
          state={tableState}
          getGroupId={(data) => data?.event?.virtId}
          getItemId={(data) =>
            data?.listing?.id ? String(data?.listing?.id) : ''
          }
          enableRowSelection={
            showCheckbox || inFullPageEventView
              ? (row) => !row.original?.isCompListing
              : false
          }
          enableMultiRowSelection={showCheckbox}
          enableSubRowSelection={showCheckbox}
          getRowId={getRowId}
          isGroupRow={(data) => Boolean(data?.isLtGrp)}
          getSubRows={getSubRows}
          renderGroupHeader={renderGroupHeader}
          sortingFns={{ ...seatingColumnSorting }}
          processRows={memoizedProcessRows}
          scrollToItemRowId={scrollTargetRowId}
          expandOnRowClick={hasExpandGroupFromClick}
        />
      </ColumnResizingContextProvider>
    </ListingTableActionContextWrapper>
  ) : (
    // We should never see this - because the events are filtered to only those that has listings
    // So this is to prevent a crash - but this should be looked at
    <NoData>
      <FormatContent
        id={FormatContentId.NoDataAvailable}
        params={listingText}
      />
    </NoData>
  );
};

export default ListingTableV2;
